import { AuthErrorCode, ServiceUnavailableErrorCode } from '@today/types'
import { MfaEnrolmentStep } from '@today/ui-domain'
import { toString as toQrString } from 'qrcode'
import { useEffect, useRef, useState } from 'react'
import { consumeMfaEnrolmentPkce, isMfaEnrolmentAvailable, mfaEnrolmentService } from '@/services/mfa-enrolment.service'

/**
 * Why a step of the TOTP enrolment journey failed (TO-63 privileged-mfa
 * REQ-013). Each value is the message key under `security:errors.*`.
 */
export enum MfaEnrolmentError {
	/** The callback's `state` is missing or differs from the stashed one — nothing was called. */
	STATE_MISMATCH = 'stateMismatch',
	/** Cognito's token endpoint refused the authorization code. */
	EXCHANGE_FAILED = 'exchangeFailed',
	/** The typed code is not 6 digits (checked before any call). */
	CODE_FORMAT = 'codeFormat',
	CODE_INVALID = 'codeInvalid',
	TOKEN_INVALID = 'tokenInvalid',
	RATE_LIMITED = 'rateLimited',
	UNAVAILABLE = 'unavailable',
	IDP_UNAVAILABLE = 'idpUnavailable',
	GENERIC = 'generic',
}

/** The API refusal codes setup/verify return, and the message each shows. */
const API_ERRORS: ReadonlyMap<string, MfaEnrolmentError> = new Map<string, MfaEnrolmentError>([
	[AuthErrorCode.MFA_CODE_INVALID, MfaEnrolmentError.CODE_INVALID],
	[AuthErrorCode.MFA_ENROLMENT_TOKEN_INVALID, MfaEnrolmentError.TOKEN_INVALID],
	[AuthErrorCode.MFA_RATE_LIMITED, MfaEnrolmentError.RATE_LIMITED],
	[AuthErrorCode.MFA_ENROLMENT_UNAVAILABLE, MfaEnrolmentError.UNAVAILABLE],
	[ServiceUnavailableErrorCode.IDP_UNAVAILABLE, MfaEnrolmentError.IDP_UNAVAILABLE],
])

/** The message for an API refusal; an unknown or missing code is the generic one. */
const enrolmentErrorOf = (error: unknown): MfaEnrolmentError => {
	const code = (error as { response?: { data?: { error?: { code?: unknown } } } } | null)?.response?.data?.error?.code
	return (typeof code === 'string' && API_ERRORS.get(code)) || MfaEnrolmentError.GENERIC
}

/** The `otpauth://` URI as an SVG data URL — no canvas needed. */
const qrCodeSrcOf = async (otpauthUri: string): Promise<string> =>
	`data:image/svg+xml;utf8,${encodeURIComponent(await toQrString(otpauthUri, { type: 'svg', margin: 2 }))}`

const SIX_DIGITS = /^[0-9]{6}$/

/** The Settings "Security" card: whether enrolment is offered, and its start action. */
export function useStartMfaEnrolment() {
	const [isStarting, setStarting] = useState(false)
	return {
		available: isMfaEnrolmentAvailable(),
		isStarting,
		start: () => {
			setStarting(true)
			void mfaEnrolmentService.start().catch(() => setStarting(false))
		},
	}
}

/** A started association: the enrolment token (memory only) and what the user scans. */
interface EnrolmentSession {
	enrolmentToken: string
	secretCode: string
	qrCodeSrc: string
}

/** The journey's state: its step, why it failed, and the live session (null once it ends). */
interface Journey {
	step: MfaEnrolmentStep
	error: MfaEnrolmentError | null
	session: EnrolmentSession | null
}

/** A journey step refused with a known reason. */
class EnrolmentFailure extends Error {
	constructor(readonly reason: MfaEnrolmentError) {
		super(reason)
	}
}

const failed = (reason: MfaEnrolmentError): Journey => ({ step: MfaEnrolmentStep.FAILED, error: reason, session: null })

/**
 * Check the callback's `state` against the (consumed) stash — refusing before
 * any call when it is missing or different — then exchange the code and start
 * the association.
 */
async function beginEnrolment(params: URLSearchParams): Promise<EnrolmentSession> {
	const stashed = consumeMfaEnrolmentPkce()
	const code = params.get('code')
	if (!stashed.verifier || !stashed.state || !code || params.get('state') !== stashed.state) {
		throw new EnrolmentFailure(MfaEnrolmentError.STATE_MISMATCH)
	}
	const enrolmentToken = await mfaEnrolmentService
		.exchangeCode({ code, verifier: stashed.verifier })
		.catch(() => Promise.reject(new EnrolmentFailure(MfaEnrolmentError.EXCHANGE_FAILED)))
	try {
		const setup = await mfaEnrolmentService.setup(enrolmentToken)
		return { enrolmentToken, secretCode: setup.secretCode, qrCodeSrc: await qrCodeSrcOf(setup.otpauthUri) }
	} catch (refusal) {
		throw new EnrolmentFailure(enrolmentErrorOf(refusal))
	}
}

/** The 6-digit code field and its verify call; an invalid code clears the field for a retry. */
function useVerifyCode(journey: Journey, setJourney: (next: Journey) => void) {
	const [code, setCode] = useState('')
	const [codeError, setCodeError] = useState<MfaEnrolmentError | null>(null)
	const [isBusy, setBusy] = useState(false)

	const submit = async () => {
		const token = journey.session?.enrolmentToken
		if (!token || isBusy) return
		if (!SIX_DIGITS.test(code)) return setCodeError(MfaEnrolmentError.CODE_FORMAT)
		setBusy(true)
		setCodeError(null)
		try {
			await mfaEnrolmentService.verify(token, code)
			setJourney({ step: MfaEnrolmentStep.DONE, error: null, session: null })
		} catch (refusal) {
			const reason = enrolmentErrorOf(refusal)
			if (reason !== MfaEnrolmentError.CODE_INVALID) return setJourney(failed(reason))
			setCode('')
			setCodeError(reason)
		} finally {
			setBusy(false)
		}
	}

	return { code, codeError, isBusy, setCode, submit }
}

/**
 * The `/settings/security/mfa/callback` journey: check `state`, exchange the
 * code, start the association, render its QR code, then verify the typed code.
 *
 * The enrolment token lives only in this hook's state — never in browser
 * storage — and is dropped once the journey ends (verified or failed).
 */
export function useMfaEnrolmentCallback(params: URLSearchParams) {
	const [journey, setJourney] = useState<Journey>({ step: MfaEnrolmentStep.LOADING, error: null, session: null })
	// The callback is single-use (the stash is consumed): run it once, even under StrictMode.
	const started = useRef(false)

	useEffect(() => {
		if (started.current) return
		started.current = true
		beginEnrolment(params).then(
			(session) => setJourney({ step: MfaEnrolmentStep.CODE, error: null, session }),
			(failure: unknown) =>
				setJourney(failed(failure instanceof EnrolmentFailure ? failure.reason : MfaEnrolmentError.GENERIC)),
		)
	}, [params])

	return {
		step: journey.step,
		error: journey.error,
		secretCode: journey.session?.secretCode ?? null,
		qrCodeSrc: journey.session?.qrCodeSrc ?? null,
		...useVerifyCode(journey, setJourney),
	}
}
